import type { TableBlockConfig } from './TableBlockConfig';
import type { ChartBlockConfig } from './ChartBlockConfig';
import type { SummaryCardBlockConfig } from './SummaryCardBlockConfig';
import { ReportBlockTypes } from '@reports/domain/types';

/**
 * Value object representing a union of all report blocks
 *
 * Encapsulates block logic with validation and business rules.
 * Immutable by design to ensure data integrity.
 *
 * @example
 * const reportBlock = createReportBlock(tableBlock);
 */
export type ReportBlock = TableBlockConfig | ChartBlockConfig | SummaryCardBlockConfig;

/**
 * Type guard to check if a block is a table block
 *
 * @param block - The block to check
 * @returns {boolean} True if the block is a table block
 */
export const isTableBlock = (block: ReportBlock): block is TableBlockConfig => {
  return block.type === ReportBlockTypes.TABLE;
};

/**
 * Type guard to check if a block is a chart block
 *
 * @param block - The block to check
 * @returns {boolean} True if the block is a chart block
 */
export const isChartBlock = (block: ReportBlock): block is ChartBlockConfig => {
  return block.type === ReportBlockTypes.CHART;
};

/**
 * Type guard to check if a block is a summary card block
 *
 * @param block - The block to check
 * @returns {boolean} True if the block is a summary card block
 */
export const isSummaryCardBlock = (block: ReportBlock): block is SummaryCardBlockConfig => {
  return block.type === ReportBlockTypes.SUMMARY_CARDS;
};
